import { computed } from 'vue';
import {
  computeCmgPayable,
  computeCmgEligibleAmount,
  computeTaxesCredit,
  computeAmountLeftPayable,
  computeAmountLeftPayableBeforeTaxes,
  computeGrossMensualization,
  computeNetMensualization,
  computeTotalAmount,
} from '../calculations/cmgCalculations';
import {
  computeTotalContributions,
} from '../calculations/socialContributions';

/**
 * All financial calculations for the CMG simulator.
 * Takes reactive refs from useContractParams as input, returns computeds.
 *
 * Equivalent to the old cmg_calculations mixin + the cost-related parts of cmgSimulator.
 */
export function useCmgCalculations(params) {
  // ===== Constants =====

  const taxLimit = 1750;
  const hourlyWageReference = 4.91;
  const effortRateHourly = {
    1: 0.0619,
    2: 0.0516,
    3: 0.0413,
    4: 0.031,
    5: 0.031,
    6: 0.031,
    7: 0.031,
    8: 0.0206,
    9: 0.0206,
  };

  // ===== Mensualization =====

  const grossMensualization = computed(() => computeGrossMensualization({
    grossMonthlyWageRegular: params.grossMonthlyWageRegular.value,
    grossMonthlyOvertimeWage: params.grossMonthlyOvertimeWage.value,
  }));

  const netMensualization = computed(() => computeNetMensualization({
    grossMonthlyWageRegular: params.grossMonthlyWageRegular.value,
    netRatioClassic: params.netRatioClassic.value,
    grossMonthlyOvertimeWage: params.grossMonthlyOvertimeWage.value,
    netRatioDetax: params.netRatioDetax.value,
    hasOvertime: params.hasOvertime.value,
  }));

  const netMensualizationClassic = computed(() => params.grossMonthlyWageRegular.value * params.netRatioClassic.value);
  const netMensualizationDetax = computed(() => params.hasOvertime.value ? (params.grossMonthlyOvertimeWage.value * params.netRatioDetax.value) : 0);
  const netMonthlyOvertimeIncrease = computed(() => params.grossMonthlyOvertimeWagePremium.value * params.netRatioDetax.value);

  const grossTotal = computed(() => grossMensualization.value + params.grossMonthlyOvertimeWagePremium.value);

  // ===== Vacations (partial year) =====

  const partialYearVacations10PercentYearlyGross = computed(() => grossMensualization.value * 12 * 0.1);
  const partialAcquiredDays = computed(() => Math.ceil(Math.min((2.5 * (params.yearlyWeeksF.value / 4)), 30)));
  const dailyMaintainVacationMaintainGross = computed(() => (grossMensualization.value * 12 / params.yearlyWeeksF.value) / 6.0);
  const partialYearVacationsMaintainsSalaryYearlyGross = computed(() => partialAcquiredDays.value * dailyMaintainVacationMaintainGross.value);
  const grossPartialYearVacationsMonthly = computed(() => {
    return Math.max(partialYearVacations10PercentYearlyGross.value, partialYearVacationsMaintainsSalaryYearlyGross.value) / 12.0;
  });
  const netVacationsMonthly = computed(() => params.isFullYear.value ? 0 : grossPartialYearVacationsMonthly.value * params.netRatioClassic.value);
  const grossVacationsMonthly = computed(() => params.isFullYear.value ? 0 : grossPartialYearVacationsMonthly.value);

  // ===== Social contributions =====

  const socialContributionsDetaxBase = computed(() => params.grossMonthlyOvertimeWage.value + params.grossMonthlyOvertimeWagePremium.value);

  const socialContributionsEmployee = computed(() => {
    return computeTotalContributions(params.socialContributions.value, 'employee', grossTotal.value, socialContributionsDetaxBase.value, params.region.value);
  });

  const socialContributionsEmployer = computed(() => {
    return computeTotalContributions(params.socialContributions.value, 'employer', grossTotal.value, socialContributionsDetaxBase.value, params.region.value);
  });

  // ===== CMG / cost =====

  const totalAmount = computed(() => computeTotalAmount({
    grossTotal: grossTotal.value,
    socialContributionsEmployer: socialContributionsEmployer.value,
    maintenanceAllowanceMonthly: params.maintenanceAllowanceMonthly.value,
    transportAllowanceMonthly: params.transportAllowanceMonthly.value,
    mealAllowanceMonthly: params.mealAllowanceMonthly.value,
  }));

  const netPaidTotal = computed(() => {
    return netMensualization.value +
      netMonthlyOvertimeIncrease.value +
      params.maintenanceAllowanceMonthly.value +
      params.transportAllowanceMonthly.value +
      params.mealAllowanceMonthly.value;
  });

  const realMonthlyRateNet = computed(() => {
    return params.maintenanceAllowanceMonthly.value +
      params.mealAllowanceMonthly.value +
      netMensualizationClassic.value +
      netMonthlyOvertimeIncrease.value;
  });
  const realHourlyRateNet = computed(() => realMonthlyRateNet.value / params.monthlyHoursF.value);
  const maxRealHourlyRate = computed(() => Math.min(realHourlyRateNet.value, params.maxNetHelp.value));
  const maxCMGHelpExceeded = computed(() => realHourlyRateNet.value > params.maxNetHelp.value);

  const socialContributionMaxHelpRatio = computed(() => {
    const rate = Math.ceil(realHourlyRateNet.value * 100) / 100;
    return rate > params.maxNetHelp.value ? Math.ceil(params.maxNetHelp.value / rate * 100) / 100 : 1;
  });
  const socialContributionsCredit = computed(() => {
    return params.cmgNotAvailable.value ? 0 : (socialContributionsEmployee.value + socialContributionsEmployer.value) * socialContributionMaxHelpRatio.value;
  });

  // ===== Methods that take parentMonthlySalaryNet =====

  function cmgPayable(parentMonthlySalaryNet) {
    return computeCmgPayable({
      maxRealHourlyRate: maxRealHourlyRate.value,
      monthlyHours: params.monthlyHoursF.value,
      parentMonthlySalaryNet,
      effortRate: effortRateHourly[params.childrenI.value],
      hourlyWageReference,
    });
  }

  function cmgEligibleAmount(parentMonthlySalaryNet) {
    return computeCmgEligibleAmount({
      cmgPayable: cmgPayable(parentMonthlySalaryNet),
      cmgNotAvailable: params.cmgNotAvailable.value,
      handicapToggle: params.handicapToggle.value,
      cmgAidToggle: params.cmgAidToggle.value,
      parentMonthlySalaryNet,
    });
  }

  function taxesCredit(parentMonthlySalaryNet) {
    return computeTaxesCredit({
      grossTotal: grossTotal.value,
      socialContributionsEmployer: socialContributionsEmployer.value,
      maintenanceAllowanceMonthlyDetaxLimit: params.maintenanceAllowanceMonthlyDetaxLimit.value,
      maintenanceAllowanceMonthly: params.maintenanceAllowanceMonthly.value,
      socialContributionsCredit: socialContributionsCredit.value,
      cmgEligibleAmount: cmgEligibleAmount(parentMonthlySalaryNet),
      otherAid: params.otherAidF.value,
      childAge: params.childAge.value,
      taxLimit,
    });
  }

  function amountLeftPayable(parentMonthlySalaryNet) {
    return computeAmountLeftPayable({
      totalAmount: totalAmount.value,
      socialContributionsCredit: socialContributionsCredit.value,
      cmgEligibleAmount: cmgEligibleAmount(parentMonthlySalaryNet),
      otherAid: params.otherAidF.value,
      taxesCredit: taxesCredit(parentMonthlySalaryNet),
    });
  }

  function amountLeftPayableBeforeTaxes(parentMonthlySalaryNet) {
    return computeAmountLeftPayableBeforeTaxes({
      totalAmount: totalAmount.value,
      socialContributionsCredit: socialContributionsCredit.value,
      cmgEligibleAmount: cmgEligibleAmount(parentMonthlySalaryNet),
      otherAid: params.otherAidF.value,
    });
  }

  function dailyCost(parentMonthlySalaryNet) {
    return amountLeftPayable(parentMonthlySalaryNet) / params.monthlyWorkedDaysF.value;
  }

  function hourlyCost(parentMonthlySalaryNet) {
    return amountLeftPayable(parentMonthlySalaryNet) / params.monthlyHoursF.value;
  }

  return {
    // Mensualization
    grossMensualization,
    netMensualization,
    netMensualizationClassic,
    netMensualizationDetax,
    netMonthlyOvertimeIncrease,
    grossTotal,

    // Vacations
    netVacationsMonthly,
    grossVacationsMonthly,

    // Social contributions
    socialContributionsEmployee,
    socialContributionsEmployer,
    socialContributionMaxHelpRatio,
    socialContributionsCredit,

    // Cost
    totalAmount,
    netPaidTotal,
    realMonthlyRateNet,
    realHourlyRateNet,
    maxRealHourlyRate,
    maxCMGHelpExceeded,

    // Methods
    cmgPayable,
    cmgEligibleAmount,
    taxesCredit,
    amountLeftPayable,
    amountLeftPayableBeforeTaxes,
    dailyCost,
    hourlyCost,
  };
}
